<template>
    <div
        :class="{ 'ask-pendo-context-button-wrapper--icon-only': iconOnly }"
        @mouseenter="isButtonHovered = true"
        @mouseleave="isButtonHovered = false">
        <pendo-button
            v-if="!iconOnly"
            class="ask-pendo-context-button"
            :class="{ 'is-active': active }"
            theme="app"
            type="secondary"
            :size="size"
            :label="$t('askPendo.Ask Leo')"
            :disabled="isDisabled"
            :aria-pressed="active"
            :aria-label="ariaLabel"
            pendo-feature="ask-pendo-context-button"
            @click="toggleConversationDrawer">
            <leo-sparkle-icon
                :spinning="!isDisabled && isButtonHovered" />
            <span>{{ $t('askPendo.Ask Leo') }}</span>
        </pendo-button>
        <pendo-button
            v-else
            v-pendo-tooltip="iconOnlyTooltipDirectiveArgs"
            class="ask-pendo-context-button--icon-only"
            :class="{ 'is-active': active }"
            theme="app"
            type="tertiary"
            :size="size"
            :disabled="isDisabled"
            :aria-label="iconOnlyTooltipContent"
            :aria-pressed="active"
            pendo-feature="ask-pendo-context-button"
            @click="toggleConversationDrawer">
            <leo-sparkle-icon :spinning="!isDisabled && isButtonHovered" />
        </pendo-button>
    </div>
</template>
<script>
import { PendoButton, PendoNotification, PendoTooltip } from '@pendo/components-vue3';
import LeoSparkleIcon from '@/ask-pendo/components/leo-sparkle-icon.vue';
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia';
import { useAskPendoStore } from '@/stores/ask-pendo.store.js';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { closeAgentModePanel, getAgentModePanelValue, openAgentModePanel, wantsNewPanelConversation } from '@/vue-router/agent-mode-panel.utils';
import { createConversationId } from '@/stores/helpers/ask-pendo.helper';
import { buildUserContext } from '@/ask-pendo/context/build-user-context';
import { leoContext } from '@/ask-pendo/context/leo-context.mixin';

export default {
    name: 'AskPendoContextButton',
    components: {
        LeoSparkleIcon,
        PendoButton,
    },
    directives: {
        PendoTooltip,
    },
    mixins: [leoContext],
    props: {
        context: {
            type: Object,
            default: () => ({}),
        },
        /** Build the context from the ancestor leoContext chain instead of the context prop. Dashboard widgets only. */
        useContextChain: {
            type: Boolean,
            default: false,
        },
        suggestedQuestions: {
            type: Array,
            default: () => [],
        },
        iconOnly: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'medium',
            validator: (size) => ['mini', 'small', 'medium'].includes(size),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Opt in to toggle behaviour: pass the drawer's open state and clicking while open closes it. */
        active: {
            type: Boolean,
            default: null,
        },
        /** Scoped to the page rather than to one thing on it (the top nav): the conversation follows the user as they navigate, until they ask something. */
        contextFollowsPage: {
            type: Boolean,
            default: false,
        },
        /** When provided, called on click to get conversation id instead of creating with prompt. Enables custom flows (e.g. table artifact). */
        getConversationId: {
            type: Function,
            default: null,
        },
        /** When provided, called at click time to generate files to attach to the conversation. */
        getFiles: {
            type: Function,
            default: null,
        },
        /** When set (icon-only mode), overrides default Ask Leo tooltip and aria-label (e.g. disabled reason). */
        iconTooltip: {
            type: String,
            default: null,
        },
        ariaLabel: {
            type: String,
            default: null,
        },
    },
    data () {
        return {
            conversationId: null,
            isButtonHovered: false,
        };
    },
    computed: {
        ...mapPiniaState(useAskPendoStore, ['conversationById']),
        ...mapPiniaState(useUserSettingsStore, ['settingByName']),
        managedAgentEnabled () {
            return this.$segmentFlag('claudeManagedAgent') && !!this.settingByName('askPendoManagedClaudeAgent', true)?.value;
        },
        // Loading only blocks starting a conversation. An open drawer must stay closable.
        isDisabled () {
            return this.disabled && !this.active;
        },
        resolvedUserContext () {
            if (this.useContextChain) {
                return buildUserContext(this.leoContextChain, this.suggestedQuestions);
            }

            return {
                ...this.context,
                suggestedQuestions: this.suggestedQuestions,
            };
        },
        iconOnlyTooltipContent () {
            if (this.iconTooltip) {
                return this.iconTooltip;
            }

            return this.$t('askPendo.Ask Leo');
        },
        iconOnlyTooltipDirectiveArgs () {
            return {
                content: this.iconOnlyTooltipContent,
                offset: 4,
            };
        },
    },
    watch: {
        resolvedUserContext: {
            deep: true,
            handler () {
                this.syncUnaskedConversation();
            },
        },
    },
    methods: {
        ...mapPiniaActions(useAskPendoStore, ['createConversation', 'setConversationUserContext']),
        /**
         * The drawer outlives the click that opened it, so a conversation pinned to the thing
         * under this button is kept in step with it. A page-following conversation is left to
         * the drawer, which tracks the route itself. Once a question has been asked nothing
         * moves, because re-scoping then would change which data the answers already on screen
         * describe.
         */
        syncUnaskedConversation () {
            if (!this.conversationId || this.contextFollowsPage) return;
            if (getAgentModePanelValue(this.$route) !== this.conversationId) return;
            if (this.conversationById(this.conversationId)?.messages?.length) return;

            this.setConversationUserContext({ id: this.conversationId, userContext: this.resolvedUserContext });
        },
        toggleConversationDrawer () {
            if (this.active) {
                this.closeConversationDrawer();

                return;
            }

            this.openConversationDrawer();
        },
        closeConversationDrawer () {
            this.conversationId = null;
            closeAgentModePanel(this.$router, this.$route, { replace: true });
        },
        // `replace` when a URL opened the panel, so Back does not land on it and open another.
        async openConversationDrawer ({ replace = false } = {}) {
            if (this.disabled) {
                return;
            }

            if (this.getConversationId) {
                this.conversationId = await this.getConversationId();
            } else {
                try {
                    this.conversationId = await createConversationId(this.managedAgentEnabled);
                } catch {
                    PendoNotification({
                        type: 'error',
                        title: this.$t('askPendo.Error creating conversation'),
                        message: this.$t('askPendo.Something went wrong while creating the conversation. Please try again.'),
                        duration: 3000,
                    });

                    return;
                }
                const files = this.getFiles ? await this.getFiles() : {};
                await this.createConversation({
                    id: this.conversationId,
                    user_context: this.resolvedUserContext,
                    files,
                    contextFollowsPage: this.contextFollowsPage,
                });
            }

            if (!this.conversationId) {
                return;
            }

            // Creating the conversation takes long enough for the user to leave the URL that
            // asked for it, and the drawer does not belong on wherever they went instead.
            if (replace && !wantsNewPanelConversation(this.$route)) {
                return;
            }

            openAgentModePanel(this.$router, this.$route, this.conversationId, { replace });
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-context-button {
    color: var(--button-text-base);
    border: 1px solid transparent;
    border-radius: 3px;
    background:
        linear-gradient(var(--button-background-ai-base), var(--button-background-ai-base)) padding-box,
        linear-gradient(to right, var(--button-border-ai-base), var(--button-border-ai-base)) border-box;
    background-origin: border-box;
    background-clip: padding-box, border-box;
    transition: box-shadow 0.25s ease;

    :deep(> span) {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    &:hover {
        color: var(--button-text-base);
        border: 1px solid transparent;
        box-shadow: none;
        background:
            linear-gradient(var(--button-background-ai-base), var(--button-background-ai-base)) padding-box,
            linear-gradient(to right, var(--static-button-border-ai-gradient-start-base), var(--static-button-border-ai-gradient-end-base)) border-box;
    }

    &.is-active,
    &.is-active:hover {
        color: var(--button-text-base);
        border: 1px solid var(--selectable-item-border-selected-ai);
        box-shadow: none;
        background: var(--button-background-ai-hover);
    }
}

.ask-pendo-context-button-wrapper--icon-only {
    display: flex;
    align-items: center;
}

.ask-pendo-context-button--icon-only {
    height: 24px;
    min-width: 24px;
    line-height: 24px;
    border-radius: 100%;
    transition: box-shadow 0.25s ease;

    &:hover {
        box-shadow: 0 2px 4px 0 rgba(var(--raw-drop-shadow-default), 0.17);
    }

    &.is-active,
    &.is-active:hover {
        box-shadow: none;
        background: var(--button-background-ai-hover);
    }
}
</style>
